<template>
  <div class="landing-page">
    <!-- Navigation -->
    <nav class="navbar" :class="{ scrolled: scrolled }">
      <div class="nav-container">
        <a href="#" class="nav-logo">
          <img src="/logo.svg" alt="Sherd" width="28" height="30">
          <span>Sherd</span>
        </a>
        
        <!-- Desktop Menu -->
        <div class="nav-menu desktop-menu">
          <a href="#how-it-works" class="nav-link">Discover</a>
          <a href="#adventures" class="nav-link">Adventures</a>
          <a href="#pwa" class="nav-link">Install</a>
          <a href="#download" class="nav-link">Download</a>
        </div>
        
        <!-- Desktop CTA -->
        <div class="nav-cta desktop-cta">
          <a href="https://play.sherdapp.com" class="nav-cta-button" target="_blank" data-umami-event="cta-nav-desktop">
            Start Adventure
          </a>
        </div>
        
        <!-- Mobile Hamburger -->
        <div class="nav-toggle" :class="{ active: mobileMenuOpen }" @click="toggleMobileMenu">
          <span class="bar"></span>
          <span class="bar"></span>
          <span class="bar"></span>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu Overlay -->
    <div class="mobile-menu-overlay" :class="{ active: mobileMenuOpen }" @click="closeMobileMenu">
      <div class="mobile-menu-content" @click.stop>
        <!-- Menu Items -->
        <div class="mobile-menu-items">
          <a href="#how-it-works" class="mobile-nav-link" @click="closeMobileMenu">Discover</a>
          <a href="#adventures" class="mobile-nav-link" @click="closeMobileMenu">Adventures</a>
          <a href="#pwa" class="mobile-nav-link" @click="closeMobileMenu">Install</a>
          <a href="#download" class="mobile-nav-link" @click="closeMobileMenu">Download</a>
        </div>
        
        <!-- Mobile CTA -->
        <div class="mobile-menu-cta">
          <a href="https://play.sherdapp.com" class="mobile-cta-button" target="_blank" @click="closeMobileMenu" data-umami-event="cta-nav-mobile">
            <span>Start Adventure</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </div>

    <!-- Hero Section -->
    <section class="hero" id="hero">
      <!-- Floating Background Elements -->
      <div class="hero-bg-elements">
        <div class="bg-element bg-element-1"></div>
        <div class="bg-element bg-element-2"></div>
        <div class="bg-element bg-element-3"></div>
        <div class="bg-element bg-element-4"></div>
      </div>
      
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">
            <span>Historical Adventure Game</span>
          </div>
          <h1 class="hero-title">
            Turn Your City Into a<br>
            <span class="highlight">Historical Adventure</span>
          </h1>
          <p class="hero-subtitle">
            Sherd transforms real locations in your city into immersive historical experiences. 
            Walk through ancient streets, explore historical sites, and solve missions based on real history.
          </p>
          <div class="hero-cta">
            <a href="https://play.sherdapp.com" class="cta-button primary" target="_blank" data-umami-event="cta-hero">
              <span>Start Your Adventure</span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
        <div class="hero-visual">
          <div class="phone-mockup">
            <div class="phone-frame">
              <div class="phone-screen">
                <img src="/phone-screenshot.png" alt="Sherd App Interface" class="phone-screenshot">
              </div>
            </div>
            
            <!-- Floating UI Elements -->
            <div class="floating-elements">
              <!-- Real Badge Item -->
              <div class="floating-element floating-badge" data-delay="0.2">
                <div class="real-badge-item">
                  <div class="real-badge-image earned">
                    <img src="/images/badge_champion.jpg" alt="Echoes of the Arena" />
                  </div>
                  <div class="badge-glow"></div>
                </div>
              </div>
              
              <!-- Mission Notification -->
              <div class="floating-element mission-notification" data-delay="0.4">
                <div class="notification-content">
                  <div class="notification-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22S19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9S10.62 6.5 12 6.5S14.5 7.62 14.5 9S13.38 11.5 12 11.5Z" fill="white"/>
                    </svg>
                  </div>
                  <div class="notification-text">
                    <div class="notification-title">New Mission Available!</div>
                    <div class="notification-subtitle">Colosseum nearby</div>
                  </div>
                </div>
              </div>
              
              <!-- Completion Badge -->
              <div class="floating-element completion-badge" data-delay="0.6">
                <div class="completion-content">
                  <span class="completion-text">COMPLETED</span>
                </div>
              </div>
              

              
              <!-- Mini Path Card -->
              <div class="floating-element mini-path-card" data-delay="0.8">
                <div class="mini-card-content">
                  <div class="mini-card-image">
                    <img src="/images/gladiator_path.jpg" alt="Gladiator Path" />
                  </div>
                  <div class="mini-card-text">
                    <div class="mini-card-title">Life of Gladiators</div>
                    <div class="mini-card-subtitle">5 missions</div>
                  </div>
                </div>
              </div>
              

            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works" id="how-it-works">
      <div class="container">
        <div class="section-content">
          <h2 class="section-title-large">
            The path<br>
            from <span class="highlight">exploration</span><br>
            → to <span class="highlight">discovery</span>
          </h2>
        </div>
        
        <!-- Desktop Layout (rimane invariato) -->
        <div class="steps-new-layout desktop-only">
          <div class="steps-grid">
            <!-- Step 1 -->
            <div class="step-card-new" :class="{ active: currentStep === 1 }" @click="setActiveStep(1)">
              <div class="step-number-circle">1</div>
              <div class="step-info">
                <h3>Open the app</h3>
                <p>Launch Sherd and let your location guide you to nearby historical adventures.</p>
              </div>
            </div>
            
            <!-- Step 2 -->
            <div class="step-card-new" :class="{ active: currentStep === 2 }" @click="setActiveStep(2)">
              <div class="step-number-circle">2</div>
              <div class="step-info">
                <h3>Choose your path</h3>
                <p>Select from curated historical adventures like "Life of Gladiators" or "Gods and Temples of Rome".</p>
              </div>
            </div>
            
            <!-- Step 3 -->
            <div class="step-card-new" :class="{ active: currentStep === 3 }" @click="setActiveStep(3)">
              <div class="step-number-circle">3</div>
              <div class="step-info">
                <h3>Walk & discover</h3>
                <p>Follow GPS-guided missions that transform real locations into immersive historical experiences.</p>
              </div>
            </div>
            
            <!-- Step 4 -->
            <div class="step-card-new" :class="{ active: currentStep === 4 }" @click="setActiveStep(4)">
              <div class="step-number-circle">4</div>
              <div class="step-info">
                <h3>Play & interact</h3>
                <p>Once you reach the location, engage with unique minigames and interactive challenges that bring history to life.</p>
              </div>
            </div>
            
            <!-- Step 5 -->
            <div class="step-card-new" :class="{ active: currentStep === 5 }" @click="setActiveStep(5)">
              <div class="step-number-circle">5</div>
              <div class="step-info">
                <h3>Earn & grow</h3>
                <p>Complete missions, solve puzzles, and earn badges as you become a true historical explorer.</p>
              </div>
            </div>
          </div>
          
          <div class="phone-showcase">
            <img :src="`/images/Step ${currentStep}.png`" :alt="`Sherd App Step ${currentStep}`" class="phone-image">
            
            <div class="showcase-controls">
              <div class="progress-indicator">
                <div class="progress-track" :style="{ width: progressWidth + '%' }"></div>
              </div>
              <button @click="toggleAutoplay" class="control-button">
                {{ isAutoplay ? '⏸️' : '▶️' }} {{ isAutoplay ? 'Pause' : 'Play' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile Layout (nuovo design scorrevole orizzontale) -->
        <div class="mobile-steps-container mobile-only">
          <div class="mobile-steps-scroll">
            <!-- Step 1 -->
            <div class="mobile-step-card" @click="setActiveStep(1)">
              <div class="mobile-step-image">
                <img src="/images/Step 1.png" alt="Open the app" loading="lazy" />
              </div>
              <div class="mobile-step-content">
                <div class="mobile-step-number">1</div>
                <h3 class="mobile-step-title">Open the app</h3>
                <p class="mobile-step-description">Launch Sherd and let your location guide you to nearby historical adventures.</p>
              </div>
            </div>
            
            <!-- Step 2 -->
            <div class="mobile-step-card" @click="setActiveStep(2)">
              <div class="mobile-step-image">
                <img src="/images/Step 2.png" alt="Choose your path" loading="lazy" />
              </div>
              <div class="mobile-step-content">
                <div class="mobile-step-number">2</div>
                <h3 class="mobile-step-title">Choose your path</h3>
                <p class="mobile-step-description">Select from curated historical adventures like "Life of Gladiators" or "Gods and Temples of Rome".</p>
              </div>
            </div>
            
            <!-- Step 3 -->
            <div class="mobile-step-card" @click="setActiveStep(3)">
              <div class="mobile-step-image">
                <img src="/images/Step 3.png" alt="Walk & discover" loading="lazy" />
              </div>
              <div class="mobile-step-content">
                <div class="mobile-step-number">3</div>
                <h3 class="mobile-step-title">Walk & discover</h3>
                <p class="mobile-step-description">Follow GPS-guided missions that transform real locations into immersive historical experiences.</p>
              </div>
            </div>
            
            <!-- Step 4 -->
            <div class="mobile-step-card" @click="setActiveStep(4)">
              <div class="mobile-step-image">
                <img src="/images/Step 4.png" alt="Play & interact" loading="lazy" />
              </div>
              <div class="mobile-step-content">
                <div class="mobile-step-number">4</div>
                <h3 class="mobile-step-title">Play & interact</h3>
                <p class="mobile-step-description">Once you reach the location, engage with unique minigames and interactive challenges that bring history to life.</p>
              </div>
            </div>
            
            <!-- Step 5 -->
            <div class="mobile-step-card" @click="setActiveStep(5)">
              <div class="mobile-step-image">
                <img src="/images/Step 5.png" alt="Earn & grow" loading="lazy" />
              </div>
              <div class="mobile-step-content">
                <div class="mobile-step-number">5</div>
                <h3 class="mobile-step-title">Earn & grow</h3>
                <p class="mobile-step-description">Complete missions, solve puzzles, and earn badges as you become a true historical explorer.</p>
              </div>
            </div>
          </div>
          
          <!-- Indicatori di scroll -->
          <div class="mobile-scroll-indicators">
            <div class="scroll-indicator" :class="{ active: currentStep === 1 }" @click="setActiveStep(1)"></div>
            <div class="scroll-indicator" :class="{ active: currentStep === 2 }" @click="setActiveStep(2)"></div>
            <div class="scroll-indicator" :class="{ active: currentStep === 3 }" @click="setActiveStep(3)"></div>
            <div class="scroll-indicator" :class="{ active: currentStep === 4 }" @click="setActiveStep(4)"></div>
            <div class="scroll-indicator" :class="{ active: currentStep === 5 }" @click="setActiveStep(5)"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Minigames Section -->
    <section class="minigames-section">
      <div class="container">
        <div class="minigames-showcase">
          <div class="minigames-visual">
            <img src="/images/minigames.png" alt="Variety of Sherd minigames including quiz, image-text, timeline, riddles, and memory games" class="minigames-image" loading="lazy">
          </div>
          
          <div class="minigames-content">
            <div class="section-tag">Interactive Gameplay</div>
            <h2 class="section-title">
              Discover History Through <span class="highlight">Interactive Minigames</span>
            </h2>
            <p class="minigames-description">
              Each location features unique challenges and puzzles that make learning history engaging and memorable. 
              Test your knowledge with <strong>quiz</strong>, explore through <strong>image-text</strong> interactions, 
              arrange events in <strong>timeline</strong> challenges, solve historical <strong>riddles</strong>, 
              play <strong>memory games</strong> with ancient artifacts, and discover many more interactive experiences 
              coming in future updates.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Adventures Section with real PathCard components -->
    <section class="adventures-section" id="adventures">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">Available Adventures</div>
          <h2 class="section-title">
            Epic <span class="highlight">Historical Paths</span> Await
          </h2>
          <p class="section-subtitle">Each adventure takes you through authentic historical locations with professionally researched stories</p>
        </div>
        <div class="adventures-column">
          <PathCard
            v-for="path in adventurePaths" 
            :key="path.id"
            :title="path.title"
            :missions-count="path.missionsCount"
            :duration="path.duration"
            :image-url="path.imageUrl"
            :card-color="path.cardColor"
            :completed="path.completed"
            :coming-soon="path.comingSoon"
            @click="handlePathClick(path)"
          />
        </div>
      </div>
    </section>

    <!-- Experience Section -->
    <section class="experience-section">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">The Sherd Experience</div>
          <h2 class="section-title">
            More Than Just a <span class="highlight">History App</span>
          </h2>
          <p class="section-subtitle">See what makes Sherd different from traditional tours and history apps</p>
        </div>
        
        <div class="experience-grid">
          <div v-for="feature in features" :key="feature.id" class="experience-item">
            <div class="experience-icon">
              <img :src="feature.iconSrc" :alt="feature.title" class="feature-icon">
            </div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Cities Section -->
    <section class="cities-section" id="cities">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">Cities</div>
          <h2 class="section-title">
            Explore <span class="highlight">Historical Cities</span>
          </h2>
          <p class="section-subtitle">Discover amazing historical adventures in these incredible cities</p>
        </div>
        
        <div class="cities-grid">
          <CityButton
            v-for="city in cities" 
            :key="city.id"
            :city-name="city.name"
            :image-url="city.imageUrl"
            :available="city.available"
            @click="handleCityClick(city)"
          />
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="testimonials-section">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">What Our Users Say</div>
          <h2 class="section-title">
            Real Stories from <span class="highlight">Real Explorers</span>
          </h2>
          <p class="section-subtitle">Discover how Sherd is transforming the way people explore history</p>
        </div>
        
        <div class="testimonials-grid">
          <div class="quote-item interactive-quote" @click="playQuoteAnimation(1)">
            <div class="quote-text typewriter">"I felt like a real gladiator walking through the arena! Now I know all about ancient Rome and can't wait for my next adventure."</div>
            <div class="quote-author">
              <div class="author-avatar">
                <div class="avatar-ring"></div>
                <div class="avatar-initial">M</div>
              </div>
              <div class="author-info">
                <strong>Marco, 28 years old,</strong><br>
                <em>loves history and exploration</em>
              </div>
            </div>

          </div>
          
          <div class="quote-item interactive-quote" @click="playQuoteAnimation(2)">
            <div class="quote-text typewriter">"Sherd made me see my own city with completely new eyes. Every street corner has a story now!"</div>
            <div class="quote-author">
              <div class="author-avatar">
                <div class="avatar-ring"></div>
                <div class="avatar-initial">S</div>
              </div>
              <div class="author-info">
                <strong>Sofia, 34 years old,</strong><br>
                <em>loves archaeology and walking tours</em>
              </div>
            </div>

          </div>
          
          <div class="quote-item interactive-quote" @click="playQuoteAnimation(3)">
            <div class="quote-text typewriter">"The storytelling blew my mind! Learning about ancient Rome while standing in the actual locations was incredible."</div>
            <div class="quote-author">
              <div class="author-avatar">
                <div class="avatar-ring"></div>
                <div class="avatar-initial">J</div>
              </div>
              <div class="author-info">
                <strong>James, 42 years old,</strong><br>
                <em>loves technology and ancient civilizations</em>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>

    <!-- Screenshots Section - Hidden -->
    <!-- 
    <section class="screenshots-section">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">App Screenshots</div>
          <h2 class="section-title">
            See Sherd in <span class="highlight">Action</span>
          </h2>
          <p class="section-subtitle">Get a glimpse of the immersive experience waiting for you</p>
        </div>
        
        <div class="screenshots-gallery">
          <div v-for="(screenshot, index) in screenshots" :key="index" class="screenshot-item">
            <img :src="screenshot.url" :alt="screenshot.title" class="screenshot-image">
            <div class="screenshot-overlay">
              <h4>{{ screenshot.title }}</h4>
              <p>{{ screenshot.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    -->

    <!-- FAQ Section -->
    <section class="faq-section" id="faq">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">Frequently Asked Questions</div>
          <h2 class="section-title">
            Got <span class="highlight">Questions</span>?
          </h2>
          <p class="section-subtitle">Everything you need to know about Sherd</p>
        </div>
        
        <div class="faq-list">
          <div v-for="faq in faqs" :key="faq.id" class="faq-item" :class="{ active: faq.isOpen }" @click="toggleFaq(faq.id)">
            <div class="faq-question">
              <h4>{{ faq.question }}</h4>
              <span class="faq-icon">{{ faq.isOpen ? '−' : '+' }}</span>
            </div>
            <div class="faq-answer" v-show="faq.isOpen">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>



    <!-- Coming Soon Section - Hidden -->
    <!-- 
    <section class="coming-soon-section">
      <div class="container">
        <div class="section-content">
          <div class="section-tag">What's Next</div>
          <h2 class="section-title">
            <span class="highlight">Coming Soon</span> Features
          </h2>
          <p class="section-subtitle">Exciting new features and cities are on the way</p>
        </div>
        
        <div class="coming-soon-grid">
          <div v-for="feature in comingSoonFeatures" :key="feature.id" class="coming-soon-card">
            <div class="coming-soon-badge">{{ feature.status }}</div>
            <div class="coming-soon-icon">{{ feature.icon }}</div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
            <div class="coming-soon-timeline">{{ feature.timeline }}</div>
          </div>
        </div>
      </div>
    </section>
    -->

    <!-- PWA Section -->
    <PWASection />

    <!-- About Creator Section -->
    <section class="creator-section">
      <div class="container">
        <div class="creator-content">
          <div class="creator-text">
            <div class="creator-intro">
              <h2 class="creator-title">Hi, I'm Luca 👋</h2>
              <p class="creator-description">
                I built Sherd to help me explore the fascinating history hidden in my own city streets. As someone who loves both coding and getting lost in historical stories, I wanted to create something that would make walking around feel like time travel.
              </p>
              <p class="creator-motivation">
                I wanted to build a historical adventure experience that felt 
                <strong>immersive</strong> to use so great storytelling and immersive gameplay was something 
                I put a lot of time into.
              </p>
              <p class="creator-hope">
                Hopefully you feel that when you use it and my app helps you discover amazing stories as much as it's helped me fall in love with history all over again :)
              </p>
              <p class="creator-contact">
                If you'd like to get in touch with me, I'd love to hear from you!
              </p>
              <div class="creator-cta">
                <a href="mailto:volinodesign@gmail.com" class="contact-button">
                  <span>Get in Touch</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
            <div class="creator-signature">
              <div class="creator-info">
                <span class="creator-name">Luca Volino</span>
                <span class="creator-role">Sherd creator</span>
              </div>
            </div>
          </div>
          <div class="creator-visual">
            <div class="creator-image-large">
              <img src="/images/luca-volino.jpg" alt="Luca Volino" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section" id="download">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">
            Ready to Explore <span class="highlight">Your City's History</span>?
          </h2>
          <p class="cta-subtitle">Join thousands of explorers discovering the hidden stories in their cities</p>
          <div class="cta-buttons">
            <a href="https://play.sherdapp.com" class="cta-button primary large" target="_blank" data-umami-event="cta-bottom">
              <span>Play Sherd Now</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>
          <div class="device-compatibility">
            <span>Optimized for mobile exploration • Available on iOS and Android</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-main">
          <div class="footer-brand-section">
            <div class="footer-brand">
              <img src="/logo.svg" alt="Sherd" width="32" height="35">
              <span>Sherd</span>
            </div>
            <p class="footer-description">
              Transform your city into an immersive historical adventure. 
              Walk through time and discover the stories hidden in your streets.
            </p>

          </div>
          
          <div class="footer-links-section">
            <div class="footer-column">
              <h4>Product</h4>
              <ul>
                <li><a href="#how-it-works">How it Works</a></li>
                <li><a href="#adventures">Adventures</a></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#cities">Cities</a></li>
              </ul>
            </div>
            
            <div class="footer-column">
              <h4>Download</h4>
              <ul>
                <li><a href="https://play.sherdapp.com" target="_blank" data-umami-event="cta-footer">Web App</a></li>
                <li><a href="#pwa">Install PWA</a></li>
              </ul>
            </div>
            

          </div>
        </div>
        
        <div class="footer-bottom">
          <div class="footer-bottom-content">
            <p>&copy; 2025 Sherd. All rights reserved.</p>
            <div class="footer-bottom-links">
              <a href="/privacy">Privacy</a>
              <span>•</span>
              <a href="/terms">Terms</a>
              <span>•</span>
              <a href="/cookies">Cookies</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import PathCard from './components/PathCard.vue'
import CityButton from './components/CityButton.vue'
import PWASection from './components/PWASection.vue'

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger)

// Reactive data
const scrolled = ref(false)
const mobileMenuOpen = ref(false)

// Adventure paths data using real Sherd structure
const adventurePaths = ref([
  {
    id: 1,
    title: 'The Life of the Gladiators',
    missionsCount: 5,
    duration: '1 hour',
    imageUrl: '/images/gladiator_path.jpg',
    cardColor: '#F8AE62',
    completed: false,
    comingSoon: false
  },
  {
    id: 2,
    title: 'The Gods and Temples of Rome',
    missionsCount: 4,
    duration: '45 min',
    imageUrl: '/images/temple_path.jpg',
    cardColor: '#018DA6',
    completed: false,
    comingSoon: true
  },
  {
    id: 3,
    title: 'The Triumphs of the Emperors',
    missionsCount: 6,
    duration: '90 min',
    imageUrl: '/images/emperor-triumph.png',
    cardColor: '#E6B8A2',
    completed: false,
    comingSoon: true
  }
])

// Steps data with videos
const steps = ref([
  {
    title: 'Walk to Historical Sites',
    description: 'Follow GPS directions to real locations in your city where historical events took place. Each spot unlocks new story chapters.',
    video: '/sherd-gameplay-1.mp4',
    poster: '/images/gladiator_path.jpg'
  },
  {
    title: 'Complete Missions',
    description: 'Solve puzzles, make choices, and interact with historical characters. Your decisions shape the story and unlock achievements.',
    video: '/sherd-gameplay-2.mp4',
    poster: '/screenshot-2.png'
  },
  {
    title: 'Unlock Achievements',
    description: 'Earn badges, collect artifacts, and track your progress as you become a master explorer of your city\'s history.',
    video: '/sherd-gameplay-1.mp4',
    poster: '/images/badge_champion.jpg'
  }
])

// Features data with real Sherd icons
const features = ref([
  {
    id: 1,
    title: 'Location-Based Storytelling',
    description: 'Stories unlock only when you\'re at the actual historical location, creating authentic connections to the past.',
    iconSrc: '/icons/lets-icons_compass.svg'
  },
  {
    id: 2,
    title: 'Interactive Minigames',
    description: 'Engage with diverse minigames including quiz, memory challenges, timeline puzzles, and riddles that make learning fun.',
    iconSrc: '/icons/lets-icons_direction-fill.svg'
  },
  {
    id: 3,
    title: 'Research-Based Content',
    description: 'Historical content is primarily sourced from Wikipedia and publicly available resources to provide educational context.',
    iconSrc: '/icons/lets-icons_home.svg'
  },
  {
    id: 4,
    title: 'Gamified Learning',
    description: 'Earn badges, unlock achievements, and track your progress as you explore different historical periods.',
    iconSrc: '/icons/lets-icons_user-alt.svg'
  }
])

// Cities data using real Sherd structure
const cities = ref([
  {
    id: 1,
    name: 'Rome',
    imageUrl: '/images/rome_city.jpg',
    available: true
  },
  {
    id: 2,
    name: 'Florence',
    imageUrl: '/images/florence_city.jpg',
    available: false
  },
  {
    id: 3,
    name: 'Venice',
    imageUrl: '/images/venice_city.jpg',
    available: false
  },
  {
    id: 4,
    name: 'Pompeii',
    imageUrl: '/images/pompeii_city.jpg',
    available: false
  }
])

// Testimonials data
const testimonials = ref([
  {
    id: 1,
    text: 'Sherd completely changed how I explore cities. Walking through Rome while learning about gladiators felt like time travel!',
    name: 'Marco Rossi',
    location: 'Rome, Italy',
    avatar: '/images/avatars/marco.jpg'
  },
  {
    id: 2,
    text: 'As a history teacher, I love how Sherd makes learning interactive. My students are obsessed with collecting badges!',
    name: 'Sarah Johnson',
    location: 'London, UK',
    avatar: '/images/avatars/sarah.jpg'
  },
  {
    id: 3,
    text: 'The location-based storytelling is genius. I discovered places in my own city I never knew existed.',
    name: 'Elena Bianchi',
    location: 'Florence, Italy',
    avatar: '/images/avatars/elena.jpg'
  }
])

// Screenshots data
const screenshots = ref([
  {
    url: '/screenshot-1.png',
    title: 'Mission Interface',
    description: 'Interactive missions guide you through historical locations'
  },
  {
    url: '/screenshot-2.png',
    title: 'Path Selection',
    description: 'Choose from multiple historical adventures'
  },
  {
    url: '/screenshot-3.png',
    title: 'Achievement System',
    description: 'Collect badges and track your progress'
  },
  {
    url: '/screenshot-4.png',
    title: 'Story Mode',
    description: 'Immersive storytelling with historical characters'
  }
])

// FAQ data
const faqs = ref([
  {
    id: 1,
    question: 'How does Sherd work?',
    answer: 'Sherd uses your phone\'s GPS to detect when you\'re at historical locations. Once there, you can unlock stories, complete missions, and learn about the history of that specific place.',
    isOpen: false
  },
  {
    id: 2,
    question: 'Do I need to be in a specific city to play?',
    answer: 'Currently, Sherd is available in Rome with more cities coming soon. Each city has its own unique historical adventures and stories.',
    isOpen: false
  },
  {
    id: 3,
    question: 'Is Sherd free to play?',
    answer: 'Yes! Sherd is currently in beta and completely free to play. We have one historical path available ("The Life of the Gladiators") that you can enjoy at no cost. Additional paths and premium features may be introduced in future updates.',
    isOpen: false
  },
  {
    id: 4,
    question: 'Can I play Sherd offline?',
    answer: 'Some content can be downloaded for offline play, but you\'ll need an internet connection for GPS verification and to unlock new content.',
    isOpen: false
  },
  {
    id: 5,
    question: 'Is the historical information accurate?',
    answer: 'Our historical content is primarily sourced from Wikipedia and other publicly available resources. While we strive for accuracy, Sherd does not assume responsibility for the absolute historical accuracy of the information provided. Users are encouraged to verify historical facts independently and use the app as an educational and entertainment tool.',
    isOpen: false
  }
])



// Coming soon features data
const comingSoonFeatures = ref([
  {
    id: 1,
    icon: '🏛️',
    title: 'New Cities',
    description: 'Florence, Venice, and Pompeii are coming with unique historical adventures.',
    status: 'In Development',
    timeline: 'Q2 2024'
  },
  {
    id: 3,
    icon: '🎧',
    title: 'Audio Tours',
    description: 'Professional narration and ambient sounds for a more immersive experience.',
    status: 'In Development',
    timeline: 'Q2 2024'
  },
  {
    id: 4,
    icon: '📱',
    title: 'AR Features',
    description: 'Augmented reality overlays to visualize historical reconstructions.',
    status: 'Research',
    timeline: 'Q4 2024'
  }
])

// Methods
const handleScroll = () => {
  const shouldBeScrolled = window.scrollY > 50
  
  // Only update if state actually changed
  if (scrolled.value !== shouldBeScrolled) {
    scrolled.value = shouldBeScrolled
  }
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
  // Prevent body scroll when menu is open
  if (mobileMenuOpen.value) {
    // Save current scroll position
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
  } else {
    // Restore scroll position
    const scrollY = document.body.style.top
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    window.scrollTo(0, parseInt(scrollY || '0') * -1)
  }
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
  // Restore scroll position
  const scrollY = document.body.style.top
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  document.body.style.overflow = ''
  if (scrollY) {
    window.scrollTo(0, parseInt(scrollY || '0') * -1)
  }
}

const handlePathClick = (path: any) => {
  if (!path.comingSoon) {
    window.open('https://play.sherdapp.com', '_blank')
  }
}

const handleCityClick = (city: any) => {
  if (city.available) {
    window.open('https://play.sherdapp.com', '_blank')
  }
}

const toggleFaq = (faqId: number) => {
  const faq = faqs.value.find(f => f.id === faqId)
  if (faq) {
    faq.isOpen = !faq.isOpen
  }
}

// Step autoplay functionality
const currentStep = ref(1)
const isAutoplay = ref(false)
const progressWidth = ref(0)
let autoplayInterval: NodeJS.Timeout | null = null
let progressInterval: NodeJS.Timeout | null = null
let resumeAutoplayTimeout: NodeJS.Timeout | null = null

const setActiveStep = (stepNumber: number) => {
  currentStep.value = stepNumber
  
  // Scroll to the corresponding mobile card if on mobile
  if (window.innerWidth <= 768) {
    scrollToMobileStep(stepNumber)
  } else {
    // Stop autoplay when user manually selects a step (only on desktop)
    isAutoplay.value = false
    stopAutoplay()
    resetProgress()
    
    // Clear any existing resume timeout
    if (resumeAutoplayTimeout) {
      clearTimeout(resumeAutoplayTimeout)
    }
    
    // Resume autoplay after 8 seconds of inactivity
    resumeAutoplayTimeout = setTimeout(() => {
      isAutoplay.value = true
      startAutoplay()
    }, 8000)
  }
}

const scrollToMobileStep = (stepNumber: number) => {
  const scrollContainer = document.querySelector('.mobile-steps-scroll')
  const card = document.querySelector(`.mobile-step-card:nth-child(${stepNumber})`) as HTMLElement
  
  if (scrollContainer && card) {
    const containerWidth = scrollContainer.clientWidth
    const cardWidth = card.offsetWidth
    const cardLeft = card.offsetLeft
    const scrollLeft = cardLeft - (containerWidth / 2) + (cardWidth / 2)
    
    scrollContainer.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    })
  }
}

const nextStep = () => {
  currentStep.value = currentStep.value >= 5 ? 1 : currentStep.value + 1
}

const startAutoplay = () => {
  // Disabilita autoplay su mobile per evitare problemi con lo scroll orizzontale
  if (window.innerWidth <= 768) {
    return
  }
  
  if (autoplayInterval) clearInterval(autoplayInterval)
  if (progressInterval) clearInterval(progressInterval)
  
  autoplayInterval = setInterval(() => {
    nextStep()
    resetProgress()
  }, 6000)
  
  startProgress()
}

const stopAutoplay = () => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval)
    autoplayInterval = null
  }
  if (progressInterval) {
    clearInterval(progressInterval)
    progressInterval = null
  }
  if (resumeAutoplayTimeout) {
    clearTimeout(resumeAutoplayTimeout)
    resumeAutoplayTimeout = null
  }
}

const toggleAutoplay = () => {
  isAutoplay.value = !isAutoplay.value
  if (isAutoplay.value) {
    startAutoplay()
  } else {
    stopAutoplay()
  }
}

const startProgress = () => {
  progressWidth.value = 0
  progressInterval = setInterval(() => {
    progressWidth.value += 1.67 // 100% in 6 seconds (100/60 = 1.67)
    if (progressWidth.value >= 100) {
      progressWidth.value = 100
    }
  }, 100)
}

const resetProgress = () => {
  progressWidth.value = 0
  if (progressInterval) {
    clearInterval(progressInterval)
  }
  if (isAutoplay.value) {
    startProgress()
  }
}

const playQuoteAnimation = (quoteId: number) => {
  const quote = document.querySelector(`.interactive-quote:nth-child(${quoteId})`)
  if (quote) {
    // Pulse animation
    gsap.to(quote, {
      scale: 1.02,
      duration: 0.1,
      yoyo: true,
      repeat: 1,
      ease: 'power2.inOut'
    })
  }
}

const likeQuote = (quoteId: number) => {
  const likeButton = document.querySelector(`.interactive-quote:nth-child(${quoteId}) .like-button`)
  if (likeButton) {
    // Heart animation
    gsap.to(likeButton.querySelector('.heart'), {
      scale: 1.5,
      duration: 0.2,
      yoyo: true,
      repeat: 1,
      ease: 'back.out(1.7)'
    })
    
    // Increment counter with animation
    const counter = likeButton.querySelector('.like-count')
    if (counter) {
      const currentCount = parseInt(counter.textContent || '0')
      counter.textContent = (currentCount + 1).toString()
      
      gsap.fromTo(counter, {
        y: -10,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        duration: 0.3,
        ease: 'back.out(1.7)'
      })
    }
  }
}

// Optimized hero entrance animation
const initHeroAnimation = () => {
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  
  // Use will-change for better performance
  const heroContent = document.querySelector('.hero-content') as HTMLElement
  const heroVisual = document.querySelector('.hero-visual') as HTMLElement
  const phoneMockup = document.querySelector('.phone-mockup') as HTMLElement
  
  if (heroContent) heroContent.style.willChange = 'opacity, transform'
  if (heroVisual) heroVisual.style.willChange = 'opacity, transform'
  if (phoneMockup) phoneMockup.style.willChange = 'transform'

  // Adjust animation based on device capabilities
  const isMobile = window.innerWidth < 768
  const duration = isMobile ? 0.4 : 0.6
  const delay = isMobile ? 0.1 : 0.2

  // Reduced duration and simpler easing for faster loading
  gsap.fromTo('.hero-content', {
    opacity: 0,
    y: prefersReducedMotion ? 0 : 20
  }, {
    opacity: 1,
    y: 0,
    duration: duration,
    ease: 'power2.out',
    onComplete: () => {
      // Clear will-change after animation
      if (heroContent) heroContent.style.willChange = 'auto'
    }
  })

  // Simpler hero visual animation
  gsap.fromTo('.hero-visual', {
    opacity: 0,
    scale: prefersReducedMotion ? 1 : 0.98
  }, {
    opacity: 1,
    scale: 1,
    duration: duration,
    delay: delay,
    ease: 'power2.out',
    onComplete: () => {
      // Clear will-change after animation
      if (heroVisual) heroVisual.style.willChange = 'auto'
    }
  })

  // Optional floating animation - only on capable devices and with motion preference
  if (!prefersReducedMotion && !isMobile && window.devicePixelRatio <= 2) {
    gsap.to('.phone-mockup', {
      y: -8,
      duration: 4,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      delay: 1,
      onComplete: () => {
        if (phoneMockup) phoneMockup.style.willChange = 'auto'
      }
    })
  }
}

// Initialize scroll animations
const initScrollAnimations = () => {
  // Skip scroll animations on desktop for specific sections
  const isDesktop = window.innerWidth > 768
  
  // ScrollTrigger for starting steps autoplay only when section is visible
  if (isDesktop) {
    console.log('Setting up ScrollTrigger for steps autoplay on desktop')
    ScrollTrigger.create({
      trigger: '.how-it-works',
      start: 'top 70%',
      end: 'bottom 30%',
      onEnter: () => {
        console.log('Steps section entered - starting autoplay')
        isAutoplay.value = true
        startAutoplay()
      },
      onLeave: () => {
        console.log('Steps section left - stopping autoplay')
        stopAutoplay()
        isAutoplay.value = false
      },
      onEnterBack: () => {
        console.log('Steps section entered back - starting autoplay')
        isAutoplay.value = true
        startAutoplay()
      },
      onLeaveBack: () => {
        console.log('Steps section left back - stopping autoplay')
        stopAutoplay()
        isAutoplay.value = false
      }
    })
  }
  // Section titles animation with enhanced effects
  gsap.utils.toArray('.section-title, .section-title-large').forEach((title: any) => {
    gsap.fromTo(title, {
      opacity: 0,
      y: 80,
      scale: 0.8,
      rotationX: 45
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      rotationX: 0,
      duration: 1.2,
      ease: 'back.out(1.2)',
      scrollTrigger: {
        trigger: title,
        start: 'top 85%',
        end: 'bottom 20%',
        toggleActions: 'play none none reverse'
      },
      onComplete: () => {
        // Attiva l'animazione del gradiente per le parole highlight all'interno del titolo
        const highlights = title.querySelectorAll('.highlight');
        highlights.forEach((highlight: any) => {
          gsap.to(highlight, {
            css: { animation: 'highlightPulse 3s ease-in-out infinite' },
            duration: 0
          });
        });
      }
    })
  })

  // Steps animation
  gsap.utils.toArray('.step-card-vertical').forEach((card: any, index: number) => {
    gsap.fromTo(card, {
      opacity: 0,
      y: 50,
      scale: 0.9
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      delay: index * 0.2,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: card,
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse'
      }
    })
  })

  // Video hover animations
  gsap.utils.toArray('.step-video-vertical').forEach((video: any) => {
    video.addEventListener('mouseenter', () => {
      gsap.to(video, { scale: 1.05, duration: 0.3, ease: 'power2.out' })
    })
    video.addEventListener('mouseleave', () => {
      gsap.to(video, { scale: 1, duration: 0.3, ease: 'power2.out' })
    })
  })

  // PathCard stagger animation - disabled on all devices
  // Animation disabled for Epic Historical Paths section

  // Experience items animation
  gsap.utils.toArray('.experience-item').forEach((item: any, index: number) => {
    gsap.fromTo(item, {
      opacity: 0,
      y: 40,
      scale: 0.8
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      delay: index * 0.1,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: item,
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse'
      }
    })
  })

  // Minigames section animations
  gsap.fromTo('.minigames-visual', {
    opacity: 0,
    x: -50,
    scale: 0.95
  }, {
    opacity: 1,
    x: 0,
    scale: 1,
    duration: 0.8,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.minigames-section',
      start: 'top 80%',
      end: 'bottom 20%',
      toggleActions: 'play none none reverse'
    }
  })

  gsap.fromTo('.minigames-content', {
    opacity: 0,
    x: 50,
    scale: 0.95
  }, {
    opacity: 1,
    x: 0,
    scale: 1,
    duration: 0.8,
    delay: 0.2,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.minigames-section',
      start: 'top 80%',
      end: 'bottom 20%',
      toggleActions: 'play none none reverse'
    }
  })

  // Creator section animation
  gsap.fromTo('.creator-text', {
    opacity: 0,
    x: -50
  }, {
    opacity: 1,
    x: 0,
    duration: 0.8,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.creator-section',
      start: 'top 80%',
      end: 'bottom 20%',
      toggleActions: 'play none none reverse'
    }
  })

  gsap.fromTo('.creator-visual', {
    opacity: 0,
    x: 50
  }, {
    opacity: 1,
    x: 0,
    duration: 0.8,
    delay: 0.2,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.creator-section',
      start: 'top 80%',
      end: 'bottom 20%',
      toggleActions: 'play none none reverse'
    }
  })

  // Creator image animation
  gsap.fromTo('.creator-image-large', {
    opacity: 0,
    y: 40,
    scale: 0.95
  }, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration: 0.8,
    delay: 0.4,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.creator-section',
      start: 'top 80%',
      end: 'bottom 20%',
      toggleActions: 'play none none reverse'
    }
  })

  // CTA section animation
  gsap.fromTo('.cta-content', {
    opacity: 0,
    y: 50,
    scale: 0.95
  }, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration: 0.8,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.cta-content',
      start: 'top 80%',
      end: 'bottom 20%',
      toggleActions: 'play none none reverse'
    }
  })

  // Cities animation
  gsap.utils.toArray('.cities-grid .city-button').forEach((city: any, index: number) => {
    gsap.fromTo(city, {
      opacity: 0,
      y: 30,
      scale: 0.9
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      delay: index * 0.1,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: city,
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse'
      }
    })
  })

  // Testimonials animation
  gsap.utils.toArray('.testimonial-card').forEach((card: any, index: number) => {
    gsap.fromTo(card, {
      opacity: 0,
      y: 40,
      scale: 0.95
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.7,
      delay: index * 0.15,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: card,
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse'
      }
    })
  })

  // Screenshots animation
  gsap.utils.toArray('.screenshot-item').forEach((item: any, index: number) => {
    gsap.fromTo(item, {
      opacity: 0,
      y: 50,
      scale: 0.9
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      delay: index * 0.2,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: item,
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse'
      }
    })
  })

  // FAQ animation - disabled on all devices
  // Animation disabled for FAQ section



  // Coming soon cards animation
  gsap.utils.toArray('.coming-soon-card').forEach((card: any, index: number) => {
    gsap.fromTo(card, {
      opacity: 0,
      y: 50,
      scale: 0.8
    }, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      delay: index * 0.2,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: card,
        start: 'top 85%',
        end: 'bottom 15%',
        toggleActions: 'play none none reverse'
      }
    })
  })
}

// Handle mobile steps scroll for auto-updating active step
const handleMobileStepsScroll = () => {
  if (window.innerWidth <= 768) {
    const scrollContainer = document.querySelector('.mobile-steps-scroll')
    if (scrollContainer) {
      const cards = scrollContainer.querySelectorAll('.mobile-step-card')
      const containerCenter = scrollContainer.scrollLeft + scrollContainer.clientWidth / 2
      
      let activeIndex = 0
      let minDistance = Infinity
      
      cards.forEach((card: any, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2
        const distance = Math.abs(containerCenter - cardCenter)
        
        if (distance < minDistance) {
          minDistance = distance
          activeIndex = index + 1
        }
      })
      
      if (currentStep.value !== activeIndex) {
        currentStep.value = activeIndex
      }
    }
  }
}

// Lifecycle
onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  
  // Start optimized hero animation
  initHeroAnimation()
  
  // Initialize scroll animations
  initScrollAnimations()
  
  // Start step autoplay only when section is in view (handled by ScrollTrigger)
  
  // Setup mobile steps scroll listener after DOM is ready
  setTimeout(() => {
    const mobileStepsScroll = document.querySelector('.mobile-steps-scroll')
    if (mobileStepsScroll) {
      mobileStepsScroll.addEventListener('scroll', handleMobileStepsScroll)
    }
  }, 100)
  
  // Clean up will-change properties after initial load
  setTimeout(() => {
    const elements = document.querySelectorAll('.hero-content, .hero-visual')
    elements.forEach((el: any) => {
      if (el.style) el.style.willChange = 'auto'
    })
  }, 2000)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  
  // Remove mobile steps scroll listener
  const mobileStepsScroll = document.querySelector('.mobile-steps-scroll')
  if (mobileStepsScroll) {
    mobileStepsScroll.removeEventListener('scroll', handleMobileStepsScroll)
  }
  
  ScrollTrigger.getAll().forEach(trigger => trigger.kill())
  stopAutoplay()
  // Reset all body styles in case menu was open
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* Sherd theme variables */
:root {
  --ion-color-primary: #EB5763;
  --ion-color-primary-shade: #d04a56;
  --ion-background-color: #FCFCFC;
  --sherd-text-primary: #1a1a1a;
  --sherd-text-secondary: #666;
}



/* Reset and Base Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.landing-page {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.6;
  color: #1a1a1a;
  background: #ffffff;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
}

/* Navigation */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0);
  backdrop-filter: blur(0px);
  border-bottom: 1px solid rgba(0, 0, 0, 0);
  z-index: 10000;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 20px;
}

.navbar.scrolled {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(25px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  padding: 15px 20px;
}

@media (max-width: 768px) {
  .navbar {
    padding: 15px 20px;
  }
  
  .nav-container {
    padding: 0;
  }
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  transition: height 0.3s ease;
}

.navbar.scrolled .nav-container {
  height: 50px;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #1a1a1a;
  font-weight: 600;
  font-size: 18px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
}

.nav-logo img {
  transition: all 0.2s ease;
}

.nav-logo:hover {
  opacity: 0.8;
}

.nav-menu {
  display: flex;
  gap: 32px;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.nav-link {
  text-decoration: none;
  color: #4a4a4a;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  padding: 8px 12px;
  border-radius: 8px;
}

.nav-link:hover {
  color: #1a1a1a;
  background: rgba(0, 0, 0, 0.04);
}

.nav-cta {
  display: flex;
  align-items: center;
}

.nav-cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  text-decoration: none;
  border-radius: 20px;
  font-weight: 700;
  font-size: 14px;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(235, 87, 99, 0.3);
  position: relative;
  overflow: hidden;
  border: none;
}

.nav-cta-button:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 10px 25px rgba(235, 87, 99, 0.4);
  background: linear-gradient(135deg, var(--ion-color-primary-shade), var(--ion-color-primary));
}

.nav-cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.nav-cta-button:hover::before {
  left: 100%;
}

.nav-toggle {
  display: none;
  flex-direction: column;
  cursor: pointer;
  z-index: 1001;
}

/* Desktop Navigation Styles */
@media (min-width: 769px) {
  /* Show desktop CTA, hide mobile menu CTA */
  .nav-cta.desktop-only {
    display: block;
  }
  
  .nav-menu .nav-cta {
    display: none;
  }
}

.bar {
  width: 22px;
  height: 2px;
  background: #1a1a1a;
  margin: 2px 0;
  transition: 0.3s;
  border-radius: 1px;
}

/* Hero Section */
.hero {
  padding: 140px 0 120px;
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  /* Simplified background for better performance */
  background: linear-gradient(135deg, #ffffff 0%, #f8fafb 100%);
}

/* Permettere overflow su laptop per vedere gli elementi fluttuanti */
@media (min-width: 1024px) and (max-width: 1439px) {
  .hero {
    overflow: visible;
  }
}

/* Reduced complexity background elements - only show on capable devices */
@media (min-width: 768px) and (prefers-reduced-motion: no-preference) {
  .hero::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.02) 0%, transparent 50%);
    z-index: 1;
    /* Removed heavy pattern animation */
  }
}

/* Removed heavy hero animations for better performance */

.hero .container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 2;
}

/* Assicurarsi che il container permetta overflow degli elementi fluttuanti */
@media (min-width: 1024px) and (max-width: 1439px) {
  .hero .container {
    overflow: visible;
  }
}

.hero-content {
  /* Initial state for entrance animation */
  opacity: 0;
  transform: translateY(20px);
  position: relative;
  z-index: 3;
  /* Performance optimizations */
  will-change: opacity, transform;
}

.hero-badge {
  display: inline-block;
  background: linear-gradient(135deg, rgba(235, 87, 99, 0.1) 0%, rgba(248, 174, 98, 0.1) 100%);
  color: #EB5763;
  padding: 12px 24px;
  border-radius: 30px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 30px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(235, 87, 99, 0.2);
  /* Removed backdrop-filter for better performance */
  box-shadow: 0 4px 15px rgba(235, 87, 99, 0.1);
}



/* Simplified Hero Background Elements - Only on capable devices */
@media (min-width: 768px) and (prefers-reduced-motion: no-preference) {
  .hero-bg-elements {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    pointer-events: none;
  }

  .bg-element {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.03), rgba(99, 102, 241, 0.03));
    /* Removed blur filter for better performance */
    opacity: 0.4;
  }

  .bg-element-1 {
    width: 120px;
    height: 120px;
    top: 15%;
    left: 20%;
    /* Removed complex animations */
  }

  .bg-element-2 {
    width: 150px;
    height: 150px;
    top: 65%;
    right: 15%;
  }

  /* Removed bg-element-3 and bg-element-4 for simplicity */
}

.hero-title {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 25px;
  color: #1a1a1a;
}

.highlight {
  background: linear-gradient(135deg, #EB5763 0%, #F8AE62 50%, #EB5763 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
}

@keyframes highlightPulse {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.hero-subtitle {
  font-size: 1rem;
  color: #666;
  margin-bottom: 35px;
  max-width: 450px;
  line-height: 1.6;
}

.hero-cta {
  margin-top: 35px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 700;
  font-size: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(235, 87, 99, 0.3);
  position: relative;
  overflow: hidden;
}

.cta-button:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 15px 35px rgba(235, 87, 99, 0.4);
  background: linear-gradient(135deg, var(--ion-color-primary-shade), var(--ion-color-primary));
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.cta-button:hover::before {
  left: 100%;
}

.cta-button.large {
  padding: 20px 40px;
  font-size: 18px;
  border-radius: 30px;
}

.device-note {
  margin-top: 20px;
  font-size: 0.9rem;
  color: #666;
  opacity: 0.8;
}

/* Hero Visual */
.hero-visual {
  position: relative;
  /* Initial state for entrance animation */
  opacity: 0;
  transform: scale(0.98);
  z-index: 3;
  /* Performance optimizations */
  will-change: opacity, transform;
}

.phone-mockup {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  /* Ensure stable initial state */
  transform: translateY(0);
  transition: none;
}

.phone-frame {
  width: 320px;
  height: 640px;
  background: #000;
  border-radius: 30px;
  padding: 8px;
  box-shadow: 
    0 0 0 2px #333,
    0 20px 60px rgba(0, 0, 0, 0.3),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  position: relative;
}

.phone-frame::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #333;
  border-radius: 2px;
}

.phone-frame::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 140px;
  height: 4px;
  background: #333;
  border-radius: 2px;
}

.phone-screen {
  width: 100%;
  height: 100%;
  border-radius: 22px;
  overflow: hidden;
  background: var(--ion-background-color);
}

.phone-screenshot {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 22px;
}

/* Floating UI Elements */
.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

/* Floating elements with float animation */
.floating-element {
  position: absolute;
  opacity: 0;
  transform: translateY(10px);
  /* Fade-in + floating animation */
  animation: floatIn 0.6s ease-out forwards, float 3s ease-in-out infinite;
  /* Restored drop-shadow for better visual effect */
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.12));
}

/* Only show floating animations on capable devices */
@media (min-width: 768px) and (prefers-reduced-motion: no-preference) {
  .floating-element[data-delay="0.2"] { 
    animation-delay: 0.2s, 1s; 
    animation-duration: 0.6s, 4s;
  }
  .floating-element[data-delay="0.4"] { 
    animation-delay: 0.4s, 1.2s; 
    animation-duration: 0.6s, 5s;
  }
  .floating-element[data-delay="0.6"] { 
    animation-delay: 0.6s, 1.4s; 
    animation-duration: 0.6s, 3.5s;
  }
  .floating-element[data-delay="0.8"] { 
    animation-delay: 0.8s, 1.6s; 
    animation-duration: 0.6s, 4.5s;
  }
}

/* Nascondi elementi fluttuanti su schermi troppo piccoli per evitare tagli */
@media (max-width: 1023px) {
  .floating-elements {
    display: none;
  }
}

/* Real Badge Item */
.floating-badge {
  top: 10%;
  left: -10%;
  animation-duration: 0.6s, 4s;
}

/* Posizionamento migliorato per laptop */
@media (min-width: 1024px) and (max-width: 1439px) {
  .floating-badge {
    left: 0%;
  }
}

.real-badge-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.real-badge-image {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #F8AE62;
  background: white;
  position: relative;
}

.real-badge-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.badge-glow {
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border-radius: 50%;
  background: linear-gradient(45deg, #F8AE62, #EB5763);
  z-index: -1;
  animation: badgeGlow 3s ease-in-out infinite;
}

/* Mission Notification */
.mission-notification {
  top: 15%;
  right: -20%;
  animation-duration: 0.6s, 5s;
}

/* Posizionamento migliorato per laptop */
@media (min-width: 1024px) and (max-width: 1439px) {
  .mission-notification {
    right: -5%;
  }
}

.notification-content {
  background: white;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-left: 4px solid #EB5763;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-icon {
  width: 32px;
  height: 32px;
  background: #EB5763;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notification-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #2D2D2D;
  line-height: 1.2;
  margin-bottom: 2px;
}

.notification-subtitle {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 11px;
  color: #737373;
  line-height: 1.2;
}

/* Completion Badge */
.completion-badge {
  top: 60%;
  right: -10%;
  animation-duration: 0.6s, 3.5s;
}

/* Posizionamento migliorato per laptop */
@media (min-width: 1024px) and (max-width: 1439px) {
  .completion-badge {
    right: 0%;
  }
}

.completion-content {
  background-color: #4CAF50;
  border-radius: 8px;
  padding: 6px 12px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
}

.completion-text {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 11px;
  line-height: 1.2;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}



/* Mini Path Card */
.mini-path-card {
  bottom: 20%;
  left: -8%;
  animation-duration: 0.6s, 4.5s;
}

/* Posizionamento migliorato per laptop */
@media (min-width: 1024px) and (max-width: 1439px) {
  .mini-path-card {
    left: 2%;
  }
}

.mini-card-content {
  background-color: #FAF6F4;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 2px 0px 0px rgba(209, 209, 209, 1);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  min-width: 140px;
}

.mini-card-image {
  width: 40px;
  height: 40px;
  background-color: #F8AE62;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.mini-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mini-card-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 11px;
  color: #2D2D2D;
  margin-bottom: 2px;
}

.mini-card-subtitle {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 10px;
  color: #737373;
}



/* Simplified Animations */
@keyframes floatIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(0.5deg); }
  50% { transform: translateY(-4px) rotate(0deg); }
  75% { transform: translateY(-12px) rotate(-0.5deg); }
}

@keyframes badgeGlow {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}



/* Section Styles */
.section-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
}

.section-tag {
  display: inline-block;
  background: #f8f9fa;
  color: #666;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 30px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: transform 0.1s ease-out;
  width: fit-content;
}

.section-title {
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(30px);
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1.1;
  color: #1a1a1a;
  margin-bottom: 30px;
  letter-spacing: -0.02em;
}

.section-title-large {
  font-size: 4rem;
  font-weight: 900;
  line-height: 1.05;
  color: #1a1a1a;
  margin-bottom: 80px;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  letter-spacing: -0.03em;
}

/* Dimensione maggiore solo su laptop e desktop */
@media (min-width: 1280px) {
  .section-title-large {
    font-size: 5rem;
  }
}

@keyframes gradientShift {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes gradientFlow {
  0%, 100% {
    background-position: 0% 0%;
  }
  33% {
    background-position: 100% 0%;
  }
  66% {
    background-position: 100% 100%;
  }
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

/* New Steps Layout */
.steps-new-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 80px;
  margin-top: 80px;
  align-items: start;
  padding-bottom: 60px;
}

.steps-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-card-new {
  display: flex;
  align-items: center;
  gap: 25px;
  padding: 30px;
  background: white;
  border-radius: 16px;
  border: 2px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.step-card-new:hover {
  border-color: #EB5763;
  box-shadow: 0 8px 25px rgba(235, 87, 99, 0.1);
  transform: translateX(5px);
}

.step-card-new.active {
  background: linear-gradient(135deg, #EB5763 0%, #F8AE62 100%);
  border-color: #EB5763;
  color: white;
  transform: translateX(10px);
  box-shadow: 0 12px 30px rgba(235, 87, 99, 0.2);
}

.step-number-circle {
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border: 3px solid #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 900;
  color: #495057;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.step-card-new.active .step-number-circle {
  background: white;
  border-color: white;
  color: #EB5763;
  transform: scale(1.1);
}

.step-info h3 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: #1a1a1a;
  transition: color 0.3s ease;
}

.step-card-new.active .step-info h3 {
  color: white;
}

.step-info p {
  font-size: 16px;
  margin: 0;
  color: #6c757d;
  line-height: 1.5;
  transition: color 0.3s ease;
}

.step-card-new.active .step-info p {
  color: rgba(255, 255, 255, 0.9);
}

.phone-showcase {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  position: sticky;
  top: 100px;
}

.phone-image {
  width: 100%;
  max-width: 350px;
  height: auto;
  border-radius: 25px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease, opacity 0.5s ease;
}

.phone-image:hover {
  transform: scale(1.02);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.2);
}

.showcase-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.progress-indicator {
  width: 200px;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-track {
  height: 100%;
  background: linear-gradient(90deg, #EB5763 0%, #F8AE62 100%);
  transition: width 0.1s linear;
  border-radius: 3px;
}

.control-button {
  background: linear-gradient(135deg, #EB5763 0%, #F8AE62 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(235, 87, 99, 0.3);
}

.control-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(235, 87, 99, 0.4);
}



.step-number-badge:hover .step-icon {
  opacity: 1;
  transform: scale(1);
}

.pulse-animation {
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.step-content-new {
  flex: 1;
  padding-top: 8px;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  line-height: 1.3;
}

.step-description {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.interactive-step {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 20px;
  border-radius: 20px;
  border: 2px solid transparent;
}

.interactive-step:hover {
  border-color: var(--ion-color-primary);
  background: rgba(235, 87, 99, 0.02);
}

/* Step Visuals */
.step-visual {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
}

.phone-mini {
  width: 40px;
  height: 60px;
  background: #1a1a1a;
  border-radius: 8px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: phoneGlow 2s ease-in-out infinite;
}

.screen-glow {
  position: absolute;
  top: 4px;
  left: 4px;
  right: 4px;
  bottom: 4px;
  background: linear-gradient(45deg, #4CAF50, #2196F3);
  border-radius: 4px;
  animation: screenFlicker 3s ease-in-out infinite;
}

.app-icon {
  position: relative;
  z-index: 2;
  font-size: 20px;
  animation: iconBounce 2s ease-in-out infinite;
}

.path-cards-mini {
  display: flex;
  gap: 10px;
}

.mini-path {
  width: 50px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mini-path.gladiator {
  background: linear-gradient(135deg, #FF6B6B, #FF8E53);
  animation: pathFloat 3s ease-in-out infinite;
}

.mini-path.temple {
  background: linear-gradient(135deg, #4ECDC4, #44A08D);
  animation: pathFloat 3s ease-in-out infinite 0.5s;
}

.mini-path.emperor {
  background: linear-gradient(135deg, #FFD93D, #FF6B6B);
  animation: pathFloat 3s ease-in-out infinite 1s;
}

.mini-path:hover {
  transform: scale(1.1) rotate(5deg);
}

.walking-animation {
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
}

.location-pin {
  font-size: 24px;
  animation: pinPulse 2s ease-in-out infinite;
}

.walking-path {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, var(--ion-color-primary), transparent);
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}

.walking-path::after {
  content: '';
  position: absolute;
  top: 0;
  left: -20px;
  width: 20px;
  height: 100%;
  background: white;
  animation: walkingProgress 2s ease-in-out infinite;
}

.discovery-burst {
  font-size: 20px;
  animation: burstAnimation 2s ease-in-out infinite;
}

.badges-collection {
  display: flex;
  gap: 8px;
}

.badge-mini {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border: 3px solid #ddd;
  transition: all 0.3s ease;
}

.badge-mini.earned {
  border-color: #F8AE62;
  background: linear-gradient(135deg, #F8AE62, #EB5763);
  color: white;
  animation: badgeShine 3s ease-in-out infinite;
}

.badge-mini.earning {
  border-color: var(--ion-color-primary);
  background: white;
  animation: badgeEarning 1s ease-in-out infinite;
}

/* Animations */
@keyframes phoneGlow {
  0%, 100% { box-shadow: 0 0 10px rgba(76, 175, 80, 0.3); }
  50% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.6); }
}

@keyframes screenFlicker {
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
}

@keyframes iconBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

@keyframes pathFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

@keyframes pinPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

@keyframes walkingProgress {
  0% { left: -20px; }
  100% { left: 60px; }
}

@keyframes burstAnimation {
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
  50% { transform: scale(1.3) rotate(180deg); opacity: 0.7; }
}

@keyframes badgeShine {
  0%, 100% { transform: scale(1); box-shadow: 0 0 10px rgba(248, 174, 98, 0.3); }
  50% { transform: scale(1.05); box-shadow: 0 0 20px rgba(248, 174, 98, 0.6); }
}

@keyframes badgeEarning {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* Focus Section */
.focus-section {
  padding: 120px 0;
  background: #fafafa;
}

.focus-content {
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
}

.focus-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: #1a1a1a;
  margin-bottom: 40px;
}

.focus-highlight {
  color: var(--ion-color-primary);
}

.focus-description {
  font-size: 1.2rem;
  color: #666;
  line-height: 1.7;
  margin: 0;
}

/* Testimonials Grid */
.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-top: 60px;
}

.quote-item {
  text-align: left;
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-left: 5px solid var(--ion-color-primary);
  position: relative;
  overflow: hidden;
}

.interactive-quote {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.interactive-quote:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border-color: var(--ion-color-primary);
}

.interactive-quote::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(235, 87, 99, 0.05), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s;
}

.interactive-quote:hover::before {
  transform: translateX(100%);
}



.quote-text {
  font-size: 1.3rem;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.6;
  margin-bottom: 30px;
  font-style: italic;
  position: relative;
  z-index: 2;
  white-space: normal;
  word-wrap: break-word;
}

.typewriter {
  /* Rimuovo l'animazione typewriter per evitare il taglio del testo */
  border-right: none;
  white-space: normal;
  word-wrap: break-word;
}

.quote-author {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
}

.author-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}

.avatar-ring {
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border: 2px solid var(--ion-color-primary);
  border-radius: 50%;
  animation: avatarPulse 2s ease-in-out infinite;
}

.avatar-initial {
  font-size: 20px;
  font-weight: 700;
  color: white;
  position: relative;
  z-index: 2;
}

.author-info {
  font-size: 1rem;
  color: #666;
  line-height: 1.5;
}

.author-info strong {
  color: #1a1a1a;
}



/* Animations for testimonials */
@keyframes typing {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes blink-caret {
  from, to { border-color: transparent; }
  50% { border-color: var(--ion-color-primary); }
}

@keyframes avatarPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
}

/* Mobile Menu Overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.mobile-menu-overlay.active {
  opacity: 1;
  visibility: visible;
}

.mobile-menu-content {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 400px;
  height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  padding: 0;
  transform: translateX(100%);
  transition: transform 0.3s ease;
}

.mobile-menu-overlay.active .mobile-menu-content {
  transform: translateX(0);
}



.mobile-menu-items {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 80px 32px 32px;
  gap: 0;
}

.mobile-nav-link {
  font-size: 20px;
  font-weight: 600;
  padding: 24px 0;
  color: #1a1a1a;
  text-decoration: none;
  border-bottom: 1px solid #f0f0f0;
  transition: color 0.2s ease;
}

.mobile-nav-link:hover {
  color: var(--ion-color-primary);
}

.mobile-nav-link:last-child {
  border-bottom: none;
}

.mobile-menu-cta {
  padding: 32px;
  border-top: 1px solid #f0f0f0;
}

.mobile-cta-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px 32px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 700;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(235, 87, 99, 0.3);
}

.mobile-cta-button:hover {
  background: linear-gradient(135deg, var(--ion-color-primary-shade), var(--ion-color-primary));
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(235, 87, 99, 0.4);
}

/* Mobile Navigation Styles */
@media (max-width: 768px) {
  .nav-container {
    padding: 0 20px;
  }

  /* Hide desktop menu and CTA on mobile */
  .desktop-menu,
  .desktop-cta {
    display: none;
  }

  .nav-toggle {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    z-index: 10001;
    position: relative;
    width: 24px;
    height: 18px;
    justify-content: space-between;
  }

  .nav-toggle .bar {
    width: 24px;
    height: 2px;
    background: #1a1a1a;
    transition: all 0.3s ease;
    border-radius: 2px;
  }

  /* Hamburger to X animation */
  .nav-toggle.active .bar:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  .nav-toggle.active .bar:nth-child(2) {
    opacity: 0;
  }

  .nav-toggle.active .bar:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -6px);
  }

  /* Ensure navbar stays on top */
  .navbar {
    z-index: 10000;
  }
}

/* Desktop Navigation Styles */
@media (min-width: 769px) {
  /* Hide mobile menu and show desktop elements */
  .mobile-menu-overlay {
    display: none;
  }
  
  .nav-toggle {
    display: none;
  }
  
  .desktop-menu,
  .desktop-cta {
    display: flex;
  }
}

/* Responsive testimonials */
@media (max-width: 768px) {
  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .quote-item {
    padding: 30px 25px;
  }
  
  .quote-text {
    font-size: 1.1rem;
  }
}

/* Responsive styles */
@media (max-width: 768px) {
  .section-title {
    font-size: 2.5rem;
  }
  
  .section-title-large {
    font-size: 3.5rem;
    max-width: 90%;
  }
  
  /* Show mobile layout, hide desktop layout */
  .desktop-only {
    display: none !important;
  }
  
  .mobile-only {
    display: block !important;
  }
  
  /* Ensure elements are visible on mobile (no scroll animations) */
  .adventures-column .path-card {
    opacity: 1 !important;
    transform: none !important;
  }
  
  .faq-item {
    opacity: 1 !important;
    transform: none !important;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 2rem;
  }
  
  .section-title-large {
    font-size: 2.8rem;
  }
}

/* Display helper classes for desktop */
@media (min-width: 769px) {
  .desktop-only {
    display: block !important;
  }
  
  .mobile-only {
    display: none !important;
  }
  
  /* Assicuriamoci che il layout desktop sia corretto */
  .steps-new-layout.desktop-only {
    display: grid !important;
    grid-template-columns: 1fr 400px !important;
    gap: 80px !important;
    align-items: start !important;
  }
  
  /* Remove scroll animation initial states on all devices */
  .adventures-column .path-card {
    opacity: 1 !important;
    transform: none !important;
  }
  
  .faq-item {
    opacity: 1 !important;
    transform: none !important;
  }
}

/* How It Works Section */
.how-it-works {
  padding: 120px 0 0;
  background: #fafafa;
}

.steps-row {
  display: flex;
  flex-direction: row;
  gap: 40px;
  margin-top: 80px;
  justify-content: center;
  align-items: flex-start;
}

.step-card-vertical {
  background: white;
  border-radius: 20px;
  padding: 35px 25px;
  text-align: center;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  transition: all 0.4s ease;
  position: relative;
  flex: 1;
  max-width: 320px;
  border: 3px solid transparent;
  backdrop-filter: blur(10px);
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(50px) scale(0.9);
}

.step-card-vertical:hover {
  transform: translateY(-12px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.18);
  border-color: rgba(235, 87, 99, 0.3);
}

.step-number {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 22px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 4px solid white;
  z-index: 10;
}

.step-content h3 {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 18px;
  margin-top: 15px;
  letter-spacing: -0.02em;
}

.step-content p {
  color: #4a5568;
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 1rem;
  font-weight: 400;
}

.step-visual-vertical {
  width: 180px;
  height: 320px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  background: #000;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
}

.step-visual-vertical::before {
  content: '';
  position: absolute;
  top: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: #333;
  border-radius: 2px;
  z-index: 2;
}

.step-visual-vertical::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: #333;
  border-radius: 2px;
  z-index: 2;
}

.step-visual-vertical:hover {
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

.step-video-vertical {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

/* Adventures Section */
.adventures-section {
  padding: 120px 0;
  background: var(--ion-background-color);
}

.adventures-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 80px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Experience Section */
.experience-section {
  padding: 120px 0;
  background: #fafafa;
}

.experience-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 80px;
}

.experience-item {
  text-align: center;
  padding: 30px 20px;
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(40px) scale(0.8);
}

.experience-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  box-shadow: 0 8px 25px rgba(235, 87, 99, 0.3);
  transition: transform 0.3s ease;
}

.experience-icon:hover {
  transform: scale(1.1);
}

.feature-icon {
  width: 30px;
  height: 30px;
  filter: brightness(0) invert(1);
}

.experience-item h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.experience-item p {
  color: #666;
  line-height: 1.6;
}

/* Creator Section */
.creator-section {
  padding: 120px 0 120px;
  background: linear-gradient(135deg, #fafbfc 0%, #f1f3f4 100%);
  position: relative;
  overflow: hidden;
}

.creator-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 15% 20%, rgba(235, 87, 99, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 85% 80%, rgba(248, 174, 98, 0.03) 0%, transparent 50%);
  z-index: 1;
}

.creator-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;
  position: relative;
  z-index: 2;
}

.creator-text {
  max-width: 600px;
}

.creator-title {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: #1a1a1a;
  margin-bottom: 30px;
}

.creator-description {
  font-size: 1.1rem;
  color: #444;
  line-height: 1.7;
  margin-bottom: 24px;
}

.creator-motivation {
  font-size: 1.1rem;
  color: #444;
  line-height: 1.7;
  margin-bottom: 24px;
}

.creator-motivation strong {
  color: #EB5763;
  font-weight: 600;
}

.creator-hope {
  font-size: 1.1rem;
  color: #444;
  line-height: 1.7;
  margin-bottom: 24px;
}

.creator-contact {
  font-size: 1.1rem;
  color: #444;
  line-height: 1.7;
  margin-bottom: 30px;
}

.creator-cta {
  margin-bottom: 40px;
}

.contact-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(235, 87, 99, 0.3);
}

.contact-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(235, 87, 99, 0.4);
  background: linear-gradient(135deg, var(--ion-color-primary-shade), var(--ion-color-primary));
}

.contact-button svg {
  transition: transform 0.3s ease;
}

.contact-button:hover svg {
  transform: translateX(2px);
}

.creator-signature {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 40px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.creator-info {
  display: flex;
  flex-direction: column;
}

.creator-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.creator-role {
  font-size: 1rem;
  color: #666;
}

.creator-avatar {
  flex-shrink: 0;
}

.avatar-circle {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: 0 8px 25px rgba(235, 87, 99, 0.3);
}

.avatar-initial {
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.creator-visual {
  position: relative;
}

.creator-image-large {
  position: relative;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.creator-image-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Responsive per creator section */
@media (max-width: 768px) {
  .creator-content {
    grid-template-columns: 1fr;
    gap: 60px;
    text-align: center;
  }
  
  /* Metto la foto prima del testo su mobile */
  .creator-visual {
    order: -1;
  }
  
  .creator-signature {
    justify-content: center;
    gap: 20px;
  }
  
  .creator-image-large {
    height: 250px;
    border-radius: 16px;
  }
}

/* CTA Section */
.cta-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.cta-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(50px) scale(0.95);
}

.cta-title {
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1.2;
  color: #1a1a1a;
  margin-bottom: 50px;
}

.cta-subtitle {
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 40px;
  line-height: 1.6;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.device-compatibility {
  margin-top: 30px;
  font-size: 1rem;
  color: #666;
  opacity: 0.8;
}

/* Footer */
.footer {
  padding: 80px 0 0;
  background: 
    radial-gradient(circle at 20% 10%, rgba(235, 87, 99, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(248, 174, 98, 0.05) 0%, transparent 50%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(235, 87, 99, 0.4), transparent);
}

.footer::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -30%;
  width: 60%;
  height: 120%;
  background: radial-gradient(circle, rgba(235, 87, 99, 0.03) 0%, transparent 70%);
  animation: footerGlow 20s ease-in-out infinite;
  z-index: 1;
}

@keyframes footerGlow {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
  50% { transform: translate(-10%, 5%) scale(1.1); opacity: 0.6; }
}



.footer-main {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 80px;
  margin-bottom: 60px;
  position: relative;
  z-index: 2;
}

.footer-brand-section {
  max-width: 350px;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 16px;
  font-weight: 700;
  font-size: 24px;
  margin-bottom: 24px;
  color: white;
}

.footer-brand img {
  filter: brightness(0) invert(1);
}

.footer-description {
  color: #94a3b8;
  line-height: 1.7;
  margin-bottom: 32px;
  font-size: 16px;
}

.footer-social {
  display: flex;
  gap: 16px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(148, 163, 184, 0.08);
  border-radius: 14px;
  color: #94a3b8;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(148, 163, 184, 0.12);
  position: relative;
  overflow: hidden;
}

.social-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
  transition: left 0.6s;
}

.social-link:hover::before {
  left: 100%;
}

.social-link:hover {
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  transform: translateY(-3px) scale(1.05);
  border-color: transparent;
  box-shadow: 
    0 12px 30px rgba(235, 87, 99, 0.4),
    0 0 0 1px rgba(235, 87, 99, 0.2);
}

.social-link:active {
  transform: translateY(-1px) scale(1.02);
}

.footer-links-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
}

.footer-column h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
  color: white;
  letter-spacing: -0.025em;
  position: relative;
  padding-bottom: 8px;
}

.footer-column h4::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 30px;
  height: 2px;
  background: linear-gradient(90deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  border-radius: 1px;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-column li {
  margin-bottom: 14px;
}

.footer-column a {
  color: #94a3b8;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  position: relative;
  padding: 4px 0;
}

.footer-column a::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  transition: width 0.3s ease;
}

.footer-column a:hover {
  color: white;
  transform: translateX(6px);
}

.footer-column a:hover::before {
  width: 100%;
}

.footer-bottom {
  padding: 32px 0;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  position: relative;
  z-index: 2;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 14px;
  position: relative;
  z-index: 2;
}

.footer-bottom-links {
  display: flex;
  align-items: center;
  gap: 20px;
}

.footer-bottom-links a {
  color: #64748b;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  padding: 4px 8px;
  border-radius: 6px;
}

.footer-bottom-links a:hover {
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.08);
  transform: translateY(-1px);
}

.footer-bottom-links span {
  color: #475569;
  font-weight: 300;
}

/* Add smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Enhance section transitions */
section {
  position: relative;
  overflow: hidden;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .container {
    padding: 0 30px;
  }
  
  .nav-container {
    padding: 0 30px;
  }
  
  .hero .container {
    grid-template-columns: 1fr;
    gap: 60px;
    text-align: center;
  }
  
  .phone-frame {
    max-width: 280px;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .section-title {
    font-size: 2.4rem;
  }
  
  .steps-row {
    flex-direction: column;
    gap: 30px;
    align-items: center;
  }
  
  .step-card-vertical {
    max-width: 400px;
  }
  
  .step-visual-vertical {
    width: 200px;
    height: 350px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }
  
  .nav-container {
    padding: 0 20px;
  }
  
  .nav-menu {
    position: fixed;
    left: -100%;
    top: 80px;
    flex-direction: column;
    background-color: rgba(255, 255, 255, 0.98);
    width: 100%;
    text-align: center;
    transition: 0.3s;
    box-shadow: 0 10px 27px rgba(0, 0, 0, 0.05);
    backdrop-filter: blur(20px);
    padding: 30px 0;
    gap: 20px;
  }

  .nav-menu.active {
    left: 0;
  }

  .nav-toggle {
    display: flex;
  }

  .nav-toggle.active .bar:nth-child(2) {
    opacity: 0;
  }

  .nav-toggle.active .bar:nth-child(1) {
    transform: translateY(4px) rotate(45deg);
  }

  .nav-toggle.active .bar:nth-child(3) {
    transform: translateY(-8px) rotate(-45deg);
  }
  
  .hero {
    padding: 120px 0 80px;
  }
  
  .phone-frame {
    max-width: 240px;
    height: 480px;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .cta-title {
    font-size: 2rem;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .footer-main {
    grid-template-columns: 1fr;
    gap: 50px;
    text-align: center;
  }
  
  .footer-brand-section {
    max-width: none;
    text-align: center;
  }
  
  .footer-brand {
    justify-content: center;
  }
  
  .footer-description {
    text-align: center;
  }
  
  .footer-social {
    justify-content: center;
  }
  
  .footer-links-section {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
    text-align: center;
  }
  
  .footer-column {
    text-align: center;
  }
  
  .footer-column h4 {
    text-align: center;
  }
  
  .footer-column h4::after {
    left: 50%;
    transform: translateX(-50%);
  }
  
  .footer-column ul {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer-column a:hover {
    transform: translateX(0);
  }
  
  .footer-bottom-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .steps-row {
    flex-direction: column;
    gap: 25px;
    align-items: center;
  }
  
  .step-card-vertical {
    padding: 30px 20px;
    max-width: 320px;
  }
  
  .step-visual-vertical {
    width: 160px;
    height: 280px;
  }
  
  .experience-grid {
    grid-template-columns: 1fr;
  }
  
  .footer-links-section {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
  
  .footer-column {
    text-align: center;
  }
  
  .footer-column h4 {
    text-align: center;
  }
  
  .footer-column h4::after {
    left: 50%;
    transform: translateX(-50%);
  }
  
  .footer-column ul {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .footer-column a:hover {
    transform: translateX(0);
  }
  
  .footer-social {
    justify-content: center;
  }
  
  .adventures-column {
    max-width: 100%;
  }
  
  .testimonials-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
  
  .screenshots-gallery {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .cities-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 30px;
  }
  

  
  .coming-soon-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
  
  /* Hide floating elements on mobile for better performance */
  .floating-elements {
    display: none;
  }
  
  /* Responsive typography */
  .section-title-large {
    font-size: 2.5rem;
  }
  
  .focus-title {
    font-size: 2.2rem;
  }
  
  .how-it-works-container {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .steps-visual {
    order: -1;
    min-height: 350px;
  }
  
  .steps-visual .phone-mockup {
    width: 220px;
    height: 440px;
  }
  
  .steps-sidebar {
    gap: 20px;
  }
  
  .autoplay-controls {
    bottom: -60px;
  }
  
  .step-item {
    padding: 20px;
    gap: 20px;
  }
  
  .step-item.active {
    transform: translateX(0);
  }
  
  .step-number-badge {
    width: 60px;
    height: 60px;
    font-size: 16px;
  }
  
  .quote-text {
    font-size: 1.2rem;
  }
}

/* Tablet adjustments for floating elements */
@media (min-width: 769px) and (max-width: 1024px) {
  .floating-elements {
    display: block;
  }
  
  /* Reduce size and adjust positions for tablet */
  .floating-badge {
    left: -15%;
    transform: scale(0.8);
  }
  
  .mission-notification {
    right: -35%;
    transform: scale(0.8);
  }
  
  .completion-badge {
    right: -15%;
    transform: scale(0.8);
  }
  
  .mini-path-card {
    left: -10%;
    transform: scale(0.8);
  }
}

/* Cities Section */
.cities-section {
  padding: 120px 0;
  background: var(--ion-background-color);
}

.cities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 40px;
  margin-top: 80px;
  justify-items: center;
}

.cities-grid .city-button {
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(30px) scale(0.9);
}

/* Testimonials Section */
.testimonials-section {
  padding: 120px 0;
  background: #fafafa;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 80px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.testimonial-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(40px) scale(0.95);
}

.testimonial-card:hover {
  transform: translateY(-5px);
}

.testimonial-stars {
  margin-bottom: 20px;
  font-size: 18px;
}

.testimonial-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 25px;
  font-style: italic;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 15px;
}

.author-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.author-name {
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 5px;
}

.author-location {
  font-size: 0.9rem;
  color: #666;
}

/* Screenshots Section */
.screenshots-section {
  padding: 120px 0;
  background: var(--ion-background-color);
}

.screenshots-gallery {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 80px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.screenshot-item {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(50px) scale(0.9);
}

.screenshot-item:hover {
  transform: translateY(-10px);
}

.screenshot-image {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.screenshot-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 30px 20px 20px;
}

.screenshot-overlay h4 {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.screenshot-overlay p {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* FAQ Section */
.faq-section {
  padding: 120px 0;
  background: #fafafa;
}

.faq-list {
  max-width: 800px;
  margin: 80px auto 0;
}

.faq-item {
  background: white;
  border-radius: 15px;
  margin-bottom: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  /* Animation disabled - elements visible by default */
  opacity: 1;
  transform: none;
}

.faq-item:hover {
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
}

.faq-question h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.faq-icon {
  font-size: 1.5rem;
  font-weight: 300;
  color: var(--ion-color-primary);
  transition: transform 0.3s ease;
}

.faq-item.active .faq-icon {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 30px 25px;
  border-top: 1px solid #f0f0f0;
}

.faq-answer p {
  color: #666;
  line-height: 1.6;
  margin: 20px 0 0;
}



/* Coming Soon Section */
.coming-soon-section {
  padding: 120px 0;
  background: #fafafa;
}

.coming-soon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 80px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.coming-soon-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
  position: relative;
  /* Initial state for scroll animation */
  opacity: 0;
  transform: translateY(50px) scale(0.8);
}

.coming-soon-card:hover {
  transform: translateY(-5px);
}

.coming-soon-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, var(--ion-color-primary), var(--ion-color-primary-shade));
  color: white;
  padding: 6px 16px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.coming-soon-icon {
  font-size: 3rem;
  margin: 20px 0;
}

.coming-soon-card h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 15px;
}

.coming-soon-card p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
}

.coming-soon-timeline {
  background: #f8f9fa;
  color: #666;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
}

/* Mobile Layout (nuovo design scorrevole orizzontale) */
.mobile-steps-container {
  margin-top: 40px;
}

.mobile-steps-scroll {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  gap: 20px;
  padding: 20px 40px 20px 40px;
  margin: 0 -20px;
  justify-content: flex-start;
}

.mobile-steps-scroll::-webkit-scrollbar {
  display: none;
}

.mobile-step-card {
  flex: 0 0 300px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  scroll-snap-align: center;
  cursor: pointer;
  height: 650px;
  display: flex;
  flex-direction: column;
}

.mobile-step-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.18);
}

.mobile-step-image {
  width: 100%;
  height: 500px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}

.mobile-step-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  background: #f8f9fa;
}

.mobile-step-content {
  padding: 20px 20px 24px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
}

.mobile-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #EB5763, #F8AE62);
  color: white;
  border-radius: 50%;
  font-size: 18px;
  font-weight: 700;
  margin: 0 auto;
  flex-shrink: 0;
}

.mobile-step-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
  flex-shrink: 0;
}

.mobile-step-description {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: flex-start;
}

.mobile-scroll-indicators {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}

.scroll-indicator {
  width: 8px;
  height: 8px;
  background: #ddd;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.scroll-indicator.active {
  background: #EB5763;
  transform: scale(1.2);
}

/* Minigames Section */
.minigames-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;
}

.minigames-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 25% 25%, rgba(235, 87, 99, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(248, 174, 98, 0.03) 0%, transparent 50%);
  z-index: 1;
}

.minigames-showcase {
  display: grid;
  grid-template-columns: 2fr 450px;
  gap: 12rem;
  align-items: center;
  margin-top: 80px;
  position: relative;
  z-index: 2;
}

.minigames-visual {
  position: relative;
}

.minigames-image {
  width: 120%;
  height: auto;
  border-radius: 20px;
  box-shadow: none;
  transition: transform 0.3s ease;
  background: transparent;
}

.minigames-image:hover {
  transform: scale(1.02);
}

.minigames-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.minigames-description {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.7;
  margin-top: 30px;
}

.minigames-description strong {
  color: #EB5763;
  font-weight: 600;
}

/* Responsive Design for Minigames Section */
@media (max-width: 1024px) {
  .minigames-showcase {
    grid-template-columns: 1fr;
    gap: 60px;
  }
  
  .minigames-content {
    text-align: center;
  }
}

@media (max-width: 768px) {
  .minigames-section {
    padding: 80px 0;
  }
  
  .minigames-showcase {
    gap: 40px;
    margin-top: 0;
    display: flex;
    flex-direction: column;
  }
  
  .minigames-visual {
    order: 2;
  }
  
  .minigames-content {
    order: 1;
  }
  
  .minigames-content .section-tag {
    text-align: center;
    margin-left: auto;
    margin-right: auto;
  }
  
  .minigames-description {
    font-size: 1rem;
    margin-top: 25px;
  }
  
  .mobile-steps-container {
    padding-bottom: 60px;
  }
}
</style> 